import {TemplateList} from "~/libs/constants/TemplateList"
import {CncSimpleArtobjectAllowedProviders} from "~/config/CncSimpleArtobjectConfig"

/**
 * Article ids whose cap has already been reported in this browser session.
 *
 * Module scope on purpose: the reporting must survive both ways ArticleDetail.vue
 * can behave on client-side navigation — it is rendered unkeyed (so Vue could reuse
 * the instance) but its page parents also wrap it in `v-if="articleStore.article"`
 * and the store nulls the article between navigations, which in practice destroys
 * and recreates it. A component-level guard would reset on that remount and report
 * the same article twice. Only ever mutated behind an `import.meta.client` check,
 * so the server never accumulates state across requests.
 */
const reportedCapArticleIds = new Set<string>()

/**
 * Embed helpers for article detail.
 *
 * `isRenderableEmbed()` is the single source of truth for "does this art object
 * actually produce an embed on the page?". It is used both by the renderer
 * (`CncSimpleArtobject.vue`) and by the counting logic in `ArticleDetail.vue`.
 * Keeping one predicate is deliberate — a duplicated condition is how the
 * `Twitter` → `X` provider rename went unnoticed.
 *
 * The filtering functions are pure and depend only on the art-object payload plus
 * the build-time template, so SSR and client produce identical results.
 */
export const useArticleEmbeds = () => {

  const EMBED_ART_OBJECT_TYPE = 'cnc_simple_artobject'

  /**
   * Art objects with this exact body are dropped by `ArtObject.vue` before any
   * component is resolved — neither they nor their children ever render.
   */
  const HIDDEN_ART_OBJECT_CONTENTS = ['_tip_', '\n']

  const isHiddenArtObject = (artObject: any): boolean => {
    return HIDDEN_ART_OBJECT_CONTENTS.includes(artObject?.contentHTML)
      || HIDDEN_ART_OBJECT_CONTENTS.includes(artObject?.rendered)
  }

  /**
   * Mirrors the render gate in `CncSimpleArtobject.vue`: an embed only renders when it
   * carries `html` AND its `provider_name` is whitelisted for the current template.
   *
   * `template` is `TemplateList | undefined` because `useUniWebTemplate().getTemplateListItem()`
   * can return undefined — never index the config without checking it first.
   */
  const isRenderableEmbed = (artObject: any, template: TemplateList | undefined): boolean => {
    if (!template) return false
    if (artObject?.type !== EMBED_ART_OBJECT_TYPE) return false
    if (isHiddenArtObject(artObject)) return false

    const embed = artObject?.attributes?.embed
    const providerName = embed?.provider_name
    if (!embed?.html || !providerName) return false

    return CncSimpleArtobjectAllowedProviders[template].includes(providerName)
  }

  /**
   * Keeps the first `limit` renderable embeds and drops the rest.
   *
   * Everything that is not a renderable embed passes through untouched — including
   * `cnc_simple_artobject`s that render nothing (paymentBox, non-whitelisted provider).
   * Those must not consume a slot, otherwise an invisible tip jar would push out a
   * visible Instagram post.
   *
   * `limit === null` (or no template) is a no-op and returns the original array by reference.
   */
  const limitRenderedEmbeds = (
    artObjects: any[],
    limit: number | null,
    template: TemplateList | undefined,
  ): any[] => {
    if (limit === null || !template) return artObjects

    const result: any[] = []
    let renderedCount = 0

    for (const artObject of artObjects) {
      if (!isRenderableEmbed(artObject, template)) {
        result.push(artObject)
        continue
      }

      if (renderedCount >= limit) continue

      renderedCount++
      result.push(artObject)
    }

    return result
  }

  /**
   * Number of renderable embeds in the list — used for logging the real impact of the cap.
   */
  const countRenderableEmbeds = (artObjects: any[], template: TemplateList | undefined): number => {
    return artObjects.reduce(
      (count, artObject) => isRenderableEmbed(artObject, template) ? count + 1 : count,
      0,
    )
  }

  /**
   * Types whose component actually re-enters `DefaultArticlesArtObject` for its
   * `content[]`, mirroring the `ArtObject.vue` switch:
   * Basic.vue (`div`/`paragraph`/`factbox`/`italic`), Heading.vue (`heading`),
   * Text.vue (`text`/`span`), Bold.vue (`bold`), Reference.vue (`reference`).
   *
   * Deliberately NOT every container: Quote.vue renders `v-html="quoteText"` and
   * BulletList.vue / OrderedList.vue render `v-html="payload?.rendered"`, so their
   * children never become art objects. Descending into them would report embeds
   * that the renderer never reaches.
   */
  const CONTENT_RECURSING_TYPES = ['div', 'paragraph', 'factbox', 'italic', 'heading', 'text', 'span', 'bold']

  const REFERENCE_TYPE = 'reference'

  /**
   * Children the renderer really descends into for this art object — same branching
   * as the components above, so a hit here means the embed would actually render.
   *
   * `reference` needs its own handling: `Reference.vue` is a `v-if`/`v-else-if`
   * chain, so it renders **one** collection at most — a quote reference with
   * `primaryText` renders neither, otherwise a non-empty `content` wins and
   * `referenceList` is never reached.
   */
  const getRenderedChildren = (artObject: any): any[] => {
    if (isHiddenArtObject(artObject)) return []

    const type = artObject?.type
    const content = Array.isArray(artObject?.content) ? artObject.content : []

    if (type === REFERENCE_TYPE) {
      const reference = artObject?.attributes?.reference
      if (reference?.type === 'quote' && reference?.primaryText) return []
      if (content.length > 0) return content

      return Array.isArray(artObject?.attributes?.referenceList) ? artObject.attributes.referenceList : []
    }

    return CONTENT_RECURSING_TYPES.includes(type) ? content : []
  }

  /**
   * Renderable embeds hiding BELOW the top level, which `limitRenderedEmbeds()` does
   * not cap: it only walks the top-level array, while the renderer descends into
   * children. Detection only — nothing is filtered.
   *
   * In the measured sample (60 HeyFomo articles, 2026-07-20..08-11) all 315 embeds
   * were top-level and the only nested types were inline ones — `text`, `bold`,
   * `list_item`, `quote_content`, `quote_author`. That is a measurement, not a
   * schema guarantee: nothing in the code or in the Content Hub contract prevents a
   * nested embed. This counter exists to notice if it ever appears, instead of the
   * cap silently leaking.
   */
  const countNestedRenderableEmbeds = (artObjects: any[], template: TemplateList | undefined): number => {
    if (!template) return 0

    let count = 0
    const queue: any[] = artObjects.flatMap(getRenderedChildren)

    while (queue.length > 0) {
      const artObject = queue.pop()
      if (isRenderableEmbed(artObject, template)) count++
      queue.push(...getRenderedChildren(artObject))
    }

    return count
  }

  /**
   * True the first time a given article's cap is reported, false on every repeat.
   * Client-only — on the server it always returns false, so SSR never reports and
   * never mutates module state.
   */
  const shouldReportCapOnce = (articleId: string | undefined): boolean => {
    if (!import.meta.client || !articleId) return false
    if (reportedCapArticleIds.has(articleId)) return false

    reportedCapArticleIds.add(articleId)
    return true
  }

  return {
    isHiddenArtObject,
    isRenderableEmbed,
    limitRenderedEmbeds,
    countRenderableEmbeds,
    countNestedRenderableEmbeds,
    shouldReportCapOnce,
  }
}
